import { OndemandCategoryTray } from '../../../domain/category-tray';
import {
  CreateCategoryPageItemRequestApiModel,
  UpdateCategoryPageItemRequestApiModel,
  CategoryPageItemApiModel,
  GradientTypeApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, remainderMapper } from 'osn-shared';
import {
  displayServiceTypeIconMapperDomainToApi,
  nameLocalizedMapperDomainToApi,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import {
  commonMapper,
  dthStatusMapper,
  nameLocalizedMapperApiToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  displayServiceTypeIconMapper,
  nameLocalizedMapperDomainToViewModel,
  scheduledDateMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  nameLocalizedMapperViewModelToDomain,
  scheduledDateMapperViewModelToDomain,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';
import { CategoryTrayFormViewModel } from './category-tray-form.view-model';

export const mapCategoryTrayDomainModelToApiModel = (
  item: OndemandCategoryTray,
  pageVersionId?: string,
  tenantId?: string
): UpdateCategoryPageItemRequestApiModel & CreateCategoryPageItemRequestApiModel => {
  const { layout, displayCategoryTitle, items, subType, gradientType } = item;

  const commonProperties = mapperPipe(
    item,
    nameLocalizedMapperDomainToApi,
    targetingMapperDomainToApi,
    displayServiceTypeIconMapperDomainToApi,
    scheduledDateMapperDomainToApi
  );

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    tenantId,
    layout,
    displayCategoryTitle,
    displayGradient: gradientType !== GradientTypeApiModel.NoGradient,
    gradientType,
    items: items.map(
      (
        { id, titleEnglish, titleArabic, secondaryTitleArabic, secondaryTitleEnglish, cardBackgroundImage, posterImage, contentGroup },
        index
      ) => ({
        id,
        titleLocalized: { en: titleEnglish || '', ar: titleArabic || '' },
        secondaryTitleLocalized: { en: secondaryTitleEnglish || '', ar: secondaryTitleArabic || '' },
        cardBackgroundImageId: cardBackgroundImage.id,
        order: index,
        posterImageId: posterImage.id,
        contentGroupId: contentGroup.id,
      })
    ),
    subType,
  }));
};

export const mapCategoryTrayApiModelToDomainModel = (categoryPageItem: CategoryPageItemApiModel): OndemandCategoryTray => {
  const { layout, displayCategoryTitle, items, subType, gradientType } = categoryPageItem;

  return remainderMapper(
    mapperPipe(
      categoryPageItem,
      commonMapper,
      nameLocalizedMapperApiToDomain,
      targetingMapperApiToDomain,
      scheduledDateMapperApiToDomain,
      displayServiceTypeIconMapperDomainToApi,
      dthStatusMapper
    ),
    () => ({
      type: 'OndemandCategoryTray',
      subType,
      layout,
      displayCategoryTitle,
      gradientType,
      items: items.map(({ id, titleLocalized, secondaryTitleLocalized, cardBackgroundImage, posterImage, contentGroup }) => ({
        id,
        titleEnglish: titleLocalized.en,
        titleArabic: titleLocalized.ar,
        secondaryTitleEnglish: secondaryTitleLocalized.en,
        secondaryTitleArabic: secondaryTitleLocalized.ar,
        cardBackgroundImage: {
          id: cardBackgroundImage.id,
          name: cardBackgroundImage.originalName,
          publicUrl: cardBackgroundImage.publicUrl,
        },
        posterImage: {
          id: posterImage.id,
          name: posterImage.originalName,
          publicUrl: posterImage.publicUrl,
        },
        contentGroup: {
          id: contentGroup.id,
          name: contentGroup.title,
          modified: contentGroup.updatedAt,
          referringPages: contentGroup.referringPageNames || [],
        },
      })),
    })
  );
};

export const mapCategoryTrayDomainModelToViewModel = (value: OndemandCategoryTray): CategoryTrayFormViewModel => {
  const { layout, displayCategoryTitle, items, subType, gradientType } = value;

  const commonProperties = mapperPipe(
    value,
    commonMapper,
    nameLocalizedMapperDomainToViewModel,
    targetingMapper,
    scheduledDateMapperDomainToViewModel,
    displayServiceTypeIconMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: TrayFormViewModelType.OndemandCategoryTray,
    subType,
    layout,
    displayCategoryTitle,
    gradientType,
    items,
  }));
};

export const mapCategoryTrayViewModelToDomainModel = (vm: CategoryTrayFormViewModel): OndemandCategoryTray => {
  const { layout, displayCategoryTitle, items, subType, gradientType } = vm;

  const commonProperties = mapperPipe(
    vm,
    commonMapper,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain,
    displayServiceTypeIconMapper
  );

  return remainderMapper(commonProperties, () => ({
    type: 'OndemandCategoryTray',
    subType,
    layout,
    displayCategoryTitle,
    gradientType,
    items,
  }));
};
